// src/pages/LoginPage2.jsx

import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setUser, fetchCurrentUser } from "../userSlice";
import { login, switchOrganization } from "../services/authService";
import { acceptInvitation } from "../services/invitationService";
import { createJoinRequest } from "../services/joinRequestService";
import { useSnackbar } from "notistack";
import { useTheme } from "@mui/material/styles";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
  Alert,
  Paper,
  useMediaQuery,
} from "@mui/material";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import { Google as GoogleIcon } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import MeetingLogo from "../assets/Meet-ing-logo.jpg";

// Styled Components
const LoginContainer = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.palette.background.default,
}));

const TopBar = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  height: "73px",
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(3, 2),
  zIndex: theme.zIndex.appBar,
  backdropFilter: 'blur(8px)',
  transition: 'box-shadow 0.2s ease, backdrop-filter 0.2s ease',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
}));



const ContactSection = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
}));

const MainContent = styled(Container)(({ theme }) => ({
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(4, 2),
  paddingTop: theme.spacing(14), // Add extra top padding for fixed header
  maxWidth: "480px",
}));

const LoginPage2 = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Form validation state
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  
  // URL parameters for invitations
  const [inviteToken, setInviteToken] = useState("");
  const [orgId, setOrgId] = useState("");
  const [orgName, setOrgName] = useState("");

  // Extract URL parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("inviteToken");
    const e = params.get("inviteEmail");
    const orgIdParam = params.get("orgId");
    const orgNameParam = params.get("orgName");
    
    if (t) setInviteToken(t);
    if (e) setEmail(e);
    if (orgIdParam) setOrgId(orgIdParam);
    if (orgNameParam) setOrgName(decodeURIComponent(orgNameParam));
  }, [location.search]);

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Form validation
  const validateEmail = (email) => {
    if (!email) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return "Please enter a valid email address";
    return "";
  };

  const validatePassword = (password) => {
    if (!password) return "Password is required";
    if (password.length < 6) return "Password must be at least 6 characters";
    return "";
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    // Only validate if user has already attempted to submit
    if (hasAttemptedSubmit) {
      setEmailError(validateEmail(value));
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    // Only validate if user has already attempted to submit
    if (hasAttemptedSubmit) {
      setPasswordError(validatePassword(value));
    }
  };

  // Social login options data
  const socialLoginOptions = [
    {
      id: "google",
      icon: GoogleIcon,
      color: "#5F6368",
    },
  ];

  const handleSocialClick = (id) => {
    const base = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const byId = {
      google: `${base}/api/auth/oauth/google/start`,
      facebook: `${base}/api/auth/oauth/facebook/start`,
      apple: `${base}/api/auth/oauth/apple/start`,
    };
    const startUrl = byId[id];
    if (!startUrl) return;
    const params = new URLSearchParams(window.location.search);
    const inviteToken = params.get('inviteToken');
    const orgId = params.get('orgId');
    const orgName = params.get('orgName');
    const u = new URL(startUrl);
    if (inviteToken) u.searchParams.set('inviteToken', inviteToken);
    if (orgId) u.searchParams.set('orgId', orgId);
    if (orgName) u.searchParams.set('orgName', orgName);
    window.location.href = u.toString();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark that user has attempted to submit (enables live validation)
    setHasAttemptedSubmit(true);
    
    // Validate form
    const emailValidation = validateEmail(email);
    const passwordValidation = validatePassword(password);
    
    setEmailError(emailValidation);
    setPasswordError(passwordValidation);
    
    if (emailValidation || passwordValidation) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const data = await login(email, password, rememberMe);
      dispatch(setUser(data.user));
      
      // Fetch fresh user data to get organization details
      const updatedUserData = await dispatch(fetchCurrentUser()).unwrap();

      enqueueSnackbar(data.message || "Logged in successfully", { variant: "success" });

      if (inviteToken) {
        try {
          await acceptInvitation(inviteToken);
          enqueueSnackbar("Invitation accepted", { variant: "success" });
          // Users joining via invitation skip onboarding
          navigate("/rooms");
          return;
        } catch (err) {
          enqueueSnackbar("Failed to accept invitation", { variant: "error" });
        }
      }

      // Handle organization join request if present
      const user = updatedUserData || data.user;
      if (orgId) {
        // Check if user is actually a member of the target organization
        const isAlreadyMember = user.organizations?.some(org => org._id === orgId);
        
        if (!isAlreadyMember) {
          // User is not a member of the target organization, submit join request
          try {
            await createJoinRequest(orgId);
            enqueueSnackbar("Join request submitted successfully! Please wait for admin approval.", { variant: "success" });
            navigate("/join-pending", { state: { organizationName: orgName } });
            return;
          } catch (err) {
            console.error("============ JOIN REQUEST FAILED ============");
            console.error("Full error object:", err);
            console.error("Error message:", err.message);
            console.error("Response status:", err.response?.status);
            console.error("Response data:", err.response?.data);
            console.error("Response headers:", err.response?.headers);
            console.error("Request config:", err.config);
            console.error("User data when making request:", user);
            console.error("orgId:", orgId);
            console.error("isAlreadyMember check result:", user.organizations?.some(org => org._id === orgId));
            console.error("User organizations:", user.organizations);
            console.error("============================================");
            enqueueSnackbar("Login successful, but failed to submit join request. Please try joining manually.", { variant: "warning" });
          }
        } else if (user.currentOrganization?._id !== orgId) {
          // User is a member but not currently in this org, switch to it
          try {
            await switchOrganization(orgId);
            dispatch(fetchCurrentUser()); // Refresh user data after switch
            enqueueSnackbar(`Switched to ${orgName}`, { variant: "success" });
            navigate("/rooms");
            return;
          } catch (err) {
            console.error("Failed to switch organization:", err);
          }
        }
      }

      // Determine redirect destination based on onboarding status
      if (user && user.currentOrganization && user.currentOrganization.onboarding && !user.currentOrganization.onboarding.isCompleted) {
        // User needs to complete onboarding
        const currentStep = user.currentOrganization.onboarding.currentStep || 'welcome';
        navigate(`/onboarding/${currentStep}`);
      } else if (user && user.currentOrganization) {
        // User has completed onboarding or doesn't need it
        navigate("/rooms");
      } else {
        // User has no organization
        navigate("/organization-choice");
      }
    } catch (err) {
      if (err.response && err.response.data?.error) {
        enqueueSnackbar(err.response.data.error, { variant: "error" });
      } else {
        enqueueSnackbar("Login failed", { variant: "error" });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <LoginContainer>
      {/* Top Bar */}
      <TopBar>
        <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
          <Box 
            component={RouterLink}
            to="/"
            sx={{ display: "flex", alignItems: "center", textDecoration: 'none' }}
          >
            <Box component="img" src={MeetingLogo} alt="Meeting logo" sx={{ width: 128, height: 47 }} />
          </Box>
        </Box>
        
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button 
            component={RouterLink} 
            to="/register" 
            variant="contained"
            sx={{
              borderRadius: "3px",
              backgroundColor: theme.palette.primary.main,
              boxShadow: theme.shadows[3],
              textTransform: "none",
              fontFamily: theme.typography.fontFamily,
              fontWeight: 500,
              fontSize: "14px",
              px: 3,
              py: 1,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                boxShadow: theme.shadows[6],
              },
            }}
          >
            Register
          </Button>
        </Box>
      </TopBar>
      
      {/* Contact Section moved to footer if needed */}
      <Box sx={{ display: "none" }}>
        <ContactSection>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary }}
          >
            Having problems?
          </Typography>
          <Link
            href="mailto:support@meetingrooms.com"
            sx={{
              color: theme.palette.text.secondary,
              textDecoration: "none",
              fontWeight: 500,
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Contact us
          </Link>
        </ContactSection>
      </Box>

      {/* Main Content */}
      <MainContent>
                {/* Login Box */}
        <Box 
          sx={{ 
            width: "391px", 
            display: "flex", 
            flexDirection: "column", 
            alignItems: "center",
            gap: 4,
            py: 4
          }}
        >
          {/* Welcome Back Title */}
          <Typography
            variant="h1"
            sx={{
              fontFamily: theme.typography.fontFamily,
              fontWeight: 500,
              color: theme.palette.text.primary,
              fontSize: "50px",
              textAlign: "center",
              mb: 2,
            }}
          >
            Welcome Back!
          </Typography>

          {orgName && (
            <Alert severity="info" sx={{ mb: 3, width: "100%" }}>
              You're logging in to join <strong>{orgName}</strong>. 
              After login, your join request will be submitted for admin approval.
            </Alert>
          )}
          
          {/* Login Section */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: theme.typography.fontFamily,
                fontWeight: 600,
                color: theme.palette.text.primary,
                fontSize: "20px",
                textAlign: "center",
              }}
            >
              Login
            </Typography>
            
            {/* Form */}
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <TextField
                fullWidth
                label="Email"
                placeholder="Enter your email"
                type="email"
                value={email}
                onChange={handleEmailChange}
                required
                variant="outlined"
                disabled={isSubmitting}
                error={!!emailError}
                helperText={emailError}
                InputProps={{
                  readOnly: !!inviteToken
                }}
                sx={{
                  width: "391px",
                  "& .MuiOutlinedInput-root": {
                    height: "60px",
                    borderRadius: theme.shape.borderRadius,
                    fontFamily: theme.typography.fontFamily,
                    fontSize: "16px",
                    transition: theme.transitions.create([
                      'border-color',
                      'box-shadow',
                      'background-color',
                    ]),
                    // Default state
                    "& fieldset": {
                      borderColor: theme.palette.divider,
                      borderWidth: "1px",
                    },
                    // Hover state
                    "&:hover fieldset": {
                      borderColor: theme.palette.primary.main,
                    },
                    // Focus state
                    "&.Mui-focused fieldset": {
                      borderColor: theme.palette.primary.main,
                      borderWidth: "2px",
                      boxShadow: `0 0 0 1px ${theme.palette.primary.main}25`,
                    },
                    // Error state
                    "&.Mui-error fieldset": {
                      borderColor: theme.palette.error.main,
                    },
                    "&.Mui-error:hover fieldset": {
                      borderColor: theme.palette.error.main,
                    },
                    "&.Mui-error.Mui-focused fieldset": {
                      borderColor: theme.palette.error.main,
                      borderWidth: "2px",
                      boxShadow: `0 0 0 1px ${theme.palette.error.main}25`,
                    },
                    // Disabled state
                    "&.Mui-disabled": {
                      backgroundColor: theme.palette.action.disabledBackground,
                      "& fieldset": {
                        borderColor: theme.palette.action.disabled,
                      },
                    },
                    // Read-only state
                    "&.Mui-readOnly": {
                      backgroundColor: theme.palette.grey[50],
                      "& fieldset": {
                        borderColor: theme.palette.grey[300],
                      },
                    },
                  },
                  "& .MuiInputBase-input": {
                    fontFamily: theme.typography.fontFamily,
                    fontSize: "16px",
                    color: theme.palette.text.primary,
                    padding: `${theme.spacing(0, 1.5)} !important`,
                    height: "60px",
                    lineHeight: "60px",
                    "&::placeholder": {
                      color: theme.palette.text.secondary,
                      opacity: 1,
                    },
                    "&:disabled": {
                      color: theme.palette.action.disabled,
                    },
                  },
                  "& .MuiInputBase-root": {
                    padding: "0 !important",
                    alignItems: "center",
                  },
                  "& .MuiInputLabel-root": {
                    color: theme.palette.text.secondary,
                    fontFamily: theme.typography.fontFamily,
                    "&.Mui-focused": {
                      color: theme.palette.primary.main,
                    },
                    "&.Mui-error": {
                      color: theme.palette.error.main,
                    },
                    "transform": "translate(12px, 20px) scale(1)",
                    "&.Mui-focused, &.MuiFormLabel-filled": {
                      transform: "translate(12px, -9px) scale(0.75)",
                    },
                  },
                  "& .MuiFormHelperText-root": {
                    fontFamily: theme.typography.fontFamily,
                    fontSize: "12px",
                    marginTop: theme.spacing(0.5),
                    "&.Mui-error": {
                      color: theme.palette.error.main,
                    },
                  },
                }}
              />
              
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={handlePasswordChange}
                required
                variant="outlined"
                disabled={isSubmitting}
                error={!!passwordError}
                helperText={passwordError}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                        disabled={isSubmitting}
                        sx={{
                          color: theme.palette.text.secondary,
                          "&:hover": {
                            color: theme.palette.primary.main,
                          },
                          "&:disabled": {
                            color: theme.palette.action.disabled,
                          },
                        }}
                      >
                        <VisibilityOffOutlined />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: "391px",
                  "& .MuiOutlinedInput-root": {
                    height: "60px",
                    borderRadius: theme.shape.borderRadius,
                    fontFamily: theme.typography.fontFamily,
                    fontSize: "16px",
                    transition: theme.transitions.create([
                      'border-color',
                      'box-shadow',
                      'background-color',
                    ]),
                    // Default state
                    "& fieldset": {
                      borderColor: theme.palette.divider,
                      borderWidth: "1px",
                    },
                    // Hover state
                    "&:hover fieldset": {
                      borderColor: theme.palette.primary.main,
                    },
                    // Focus state
                    "&.Mui-focused fieldset": {
                      borderColor: theme.palette.primary.main,
                      borderWidth: "2px",
                      boxShadow: `0 0 0 1px ${theme.palette.primary.main}25`,
                    },
                    // Error state
                    "&.Mui-error fieldset": {
                      borderColor: theme.palette.error.main,
                    },
                    "&.Mui-error:hover fieldset": {
                      borderColor: theme.palette.error.main,
                    },
                    "&.Mui-error.Mui-focused fieldset": {
                      borderColor: theme.palette.error.main,
                      borderWidth: "2px",
                      boxShadow: `0 0 0 1px ${theme.palette.error.main}25`,
                    },
                    // Disabled state
                    "&.Mui-disabled": {
                      backgroundColor: theme.palette.action.disabledBackground,
                      "& fieldset": {
                        borderColor: theme.palette.action.disabled,
                      },
                    },
                  },
                  "& .MuiInputBase-input": {
                    fontFamily: theme.typography.fontFamily,
                    fontSize: "16px",
                    color: theme.palette.text.primary,
                    padding: `${theme.spacing(0, 1.5)} !important`,
                    paddingRight: "0 !important", // Remove right padding for endAdornment
                    height: "60px",
                    lineHeight: "60px",
                    "&::placeholder": {
                      color: theme.palette.text.secondary,
                      opacity: 1,
                    },
                    "&:disabled": {
                      color: theme.palette.action.disabled,
                    },
                  },
                  "& .MuiInputBase-root": {
                    padding: "0 !important",
                    paddingRight: `${theme.spacing(1.5)} !important`, // Add right padding for endAdornment
                    alignItems: "center",
                  },
                  "& .MuiInputLabel-root": {
                    color: theme.palette.text.secondary,
                    fontFamily: theme.typography.fontFamily,
                    "&.Mui-focused": {
                      color: theme.palette.primary.main,
                    },
                    "&.Mui-error": {
                      color: theme.palette.error.main,
                    },
                    "transform": "translate(12px, 20px) scale(1)",
                    "&.Mui-focused, &.MuiFormLabel-filled": {
                      transform: "translate(12px, -9px) scale(0.75)",
                    },
                  },
                  "& .MuiFormHelperText-root": {
                    fontFamily: theme.typography.fontFamily,
                    fontSize: "12px",
                    marginTop: theme.spacing(0.5),
                    "&.Mui-error": {
                      color: theme.palette.error.main,
                    },
                  },
                }}
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={isSubmitting}
                    sx={{
                      width: "25px",
                      height: "25px",
                      color: theme.palette.text.primary,
                      transition: theme.transitions.create(['color']),
                      "&:hover": {
                        color: theme.palette.primary.main,
                      },
                      "&.Mui-checked": {
                        color: theme.palette.primary.main,
                      },
                      "&.Mui-disabled": {
                        color: theme.palette.action.disabled,
                      },
                    }}
                  />
                }
                label="Remember me"
                disabled={isSubmitting}
                sx={{
                  width: "391px",
                  alignSelf: "flex-start",
                  ml: "0px",
                  "& .MuiFormControlLabel-label": {
                    fontFamily: theme.typography.fontFamily,
                    fontSize: "16px",
                    color: theme.palette.text.primary,
                    transition: theme.transitions.create(['color']),
                    "&.Mui-disabled": {
                      color: theme.palette.action.disabled,
                    },
                  },
                }}
              />
              
              <Button
                variant="contained"
                type="submit"
                fullWidth
                disabled={isSubmitting || !!emailError || !!passwordError}
                sx={{
                  width: "391px",
                  height: "60px",
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: theme.shape.borderRadius,
                  boxShadow: theme.customShadows.primaryButton,
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: 600,
                  fontSize: "16px",
                  textTransform: "none",
                  transition: theme.transitions.create([
                    'background-color',
                    'box-shadow',
                    'opacity',
                  ]),
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                    boxShadow: theme.customShadows.primaryButtonHover,
                  },
                  "&:disabled": {
                    backgroundColor: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                    boxShadow: "none",
                  },
                }}
              >
                {isSubmitting ? "Signing in..." : "Login"}
              </Button>
            </Box>
            
            {/* Forgot Password Link */}
            <Link
              component={RouterLink}
              to="/forgot-password"
              underline="none"
              sx={{
                fontFamily: theme.typography.fontFamily,
                fontWeight: 500,
                color: isSubmitting ? theme.palette.action.disabled : theme.palette.primary.main,
                fontSize: "14px",
                textAlign: "center",
                mt: 1,
                transition: theme.transitions.create(['color']),
                pointerEvents: isSubmitting ? "none" : "auto",
                "&:hover": {
                  color: isSubmitting ? theme.palette.action.disabled : theme.palette.primary.dark,
                  textDecoration: "underline",
                },
              }}
            >
              Forgot your password?
            </Link>
          </Box>
          
          {/* Social Login Section */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
              mt: 4,
            }}
          >
            {/* Login with divider */}
            <Box 
              sx={{ 
                width: "280px", 
                display: "flex", 
                alignItems: "center", 
                gap: 2 
              }}
            >
              <Divider sx={{ flexGrow: 1, backgroundColor: theme.palette.text.disabled }} />
              <Typography
                sx={{
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: 400,
                  color: theme.palette.text.disabled,
                  fontSize: "14px",
                  whiteSpace: "nowrap",
                }}
              >
                Login with
              </Typography>
              <Divider sx={{ flexGrow: 1, backgroundColor: theme.palette.text.disabled }} />
            </Box>
            
            {/* Social Icons */}
            <Box
              sx={{
                width: "280px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <IconButton
                onClick={() => handleSocialClick('google')}
                sx={{
                  width: "67px",
                  height: "67px",
                  borderRadius: "33.5px",
                  border: `1px solid ${theme.palette.text.disabled}`,
                }}
              >
                <GoogleIcon 
                  sx={{ 
                    color: "#5F6368",
                    fontSize: "30px"
                  }} 
                />
              </IconButton>
            </Box>
          </Box>
          
          {/* Sign Up Link */}
          <Link
            component={RouterLink}
            to="/register"
            underline="none"
            sx={{
              fontFamily: theme.typography.fontFamily,
              fontWeight: 400,
              color: theme.palette.primary.main,
              fontSize: "16px",
              textAlign: "center",
              mt: 4,
            }}
          >
            Don't have an account yet? Sign up here
          </Link>
        </Box>
      </MainContent>
    </LoginContainer>
  );
};

export default LoginPage2;
